import { tilesetDisplayName, type CataloguePropInfo, type EditorCatalogue } from "./catalogue.ts";
import { packOf } from "./packs.ts";

/**
 * Catalogue props as the prop palette lists them (ADR-026 amendment, R-0017): headed by their pack's
 * display name and the folder their art lies in, tagged by how they play, filtered by text, tag and pack,
 * and sorted four ways. Everything here reads the catalogue alone: pack names are the host's, handed in as
 * a map, and no pack, folder or tag vocabulary is written in code. Names and folders compare in natural
 * order without regard to case, by code unit rather than by locale, so every browser and Bun agree.
 */

/**
 * A prop's tag: `static` for a still, `animated` for a strip that loops, `once` for a transition strip.
 */
export type PropTag = "static" | "animated" | "once";

/**
 * The tags a palette offers, in its order, with their labels.
 */
export const PROP_TAGS: readonly { readonly tag: PropTag; readonly label: string }[] = [
  { tag: "static", label: "Static" },
  { tag: "animated", label: "Animated" },
  { tag: "once", label: "Plays once" },
];

/**
 * A sort order: `folder` by heading, then group, then name; `name` by group, then heading, then name;
 * `animated` groups holding a loop, then a strip that plays once, then stills only; `static` groups holding
 * a still, then a loop, then a strip that plays once; the last two each in `folder` order. A group ranks by
 * the first of its props in the order, so every order keeps a group's props together as one run (P1.6-T12).
 */
export type PropOrder = "folder" | "name" | "animated" | "static";

/**
 * The sort orders a palette offers, the default first, with their labels.
 */
export const PROP_ORDERS: readonly { readonly order: PropOrder; readonly label: string }[] = [
  { order: "folder", label: "Folder" },
  { order: "name", label: "Name" },
  { order: "animated", label: "Animated first" },
  { order: "static", label: "Static first" },
];

/**
 * Display names of packs by id, as the host gives them.
 */
export type PackNames = ReadonlyMap<string, string>;

/**
 * Where the palette heads a prop. `pack` is its tileset's prefix (empty for a tileset without one) and
 * `packName` that pack's display name, or its id when the host names none. `folders` is the folder path
 * of the prop's path in its pack, or, for a prop of a catalogue written before paths (`from` is `sheet`),
 * its sheet's display name. `key` is equal for two props exactly when they share a heading, folders
 * compared without regard to case. `label` joins the pack name and the folders with ` / `.
 */
export interface PropHeading {
  readonly key: string;
  readonly pack: string;
  readonly packName: string;
  readonly folders: readonly string[];
  readonly from: "path" | "sheet";
  readonly label: string;
}

/**
 * The props under one heading.
 */
export interface HeadedProps {
  readonly heading: PropHeading;
  readonly props: readonly CataloguePropInfo[];
}

/**
 * What a palette narrows its props by. `search` is read as words split by white space, without regard to
 * case, and keeps a prop when each word is found in its name, its group or, when `searchHeadings` gives the
 * pack names to label it with, the label of its heading (its pack's display name and its folders, or an
 * older catalogue's sheet; ADR-026 amendment, decision 6), each word in any of them and in any order
 * (P1.6-T12); `tags` keeps the props carrying any of them; `packs` keeps the props of any of them. Absent or
 * empty, each keeps everything, and all three must match.
 */
export interface PropFilter {
  readonly search?: string;
  readonly searchHeadings?: PackNames;
  readonly tags?: readonly PropTag[];
  readonly packs?: readonly string[];
}

const LABEL_SEPARATOR = " / ";

const TAG_BY_PLAYBACK: ReadonlyMap<string, PropTag> = new Map([
  ["static", "static"],
  ["loop", "animated"],
  ["once", "once"],
]);

const ANIMATED_FIRST: ReadonlyMap<string, number> = new Map([
  ["loop", 0],
  ["once", 1],
  ["static", 2],
]);

const STATIC_FIRST: ReadonlyMap<string, number> = new Map([
  ["static", 0],
  ["loop", 1],
  ["once", 2],
]);

const LEADING_ZEROS = /^0+(?=\d)/;

/**
 * A prop with its heading and its group's rank in the order, worked out once for a sort.
 */
interface Headed {
  readonly prop: CataloguePropInfo;
  readonly heading: PropHeading;
  readonly rank: number;
}

/**
 * The tags of a prop: the one its playback gives, or none for a prop of a catalogue written before
 * playback, or with a playback this build does not know.
 *
 * @param prop The prop.
 * @returns Its tags.
 */
export function propTags(prop: CataloguePropInfo): readonly PropTag[] {
  const tag = prop.plays === undefined ? undefined : TAG_BY_PLAYBACK.get(prop.plays);
  return tag === undefined ? [] : [tag];
}

/**
 * The display names of the packs a catalogue names.
 *
 * @param catalogue The catalogue.
 * @returns Names by pack id; empty for a catalogue written before it named packs.
 */
export function packNamesOf(catalogue: EditorCatalogue): PackNames {
  return new Map((catalogue.packs ?? []).map((pack) => [pack.id, pack.name]));
}

/**
 * The folders of a path in a pack: every segment but the last.
 *
 * @param path The path, forward slashes.
 * @returns The folders, outermost first; empty at the pack's root.
 */
function foldersOf(path: string): string[] {
  return path.split("/").filter((segment) => segment !== "").slice(0, -1);
}

/**
 * Where the palette heads a prop: its pack and the folders its path lies in, or, for a prop of a catalogue
 * written before paths, its pack and its sheet's display name. A prop with neither a pack nor a folder is
 * headed by its sheet's display name.
 *
 * @param prop The prop.
 * @param packNames Display names of packs by id.
 * @returns The heading.
 */
export function propHeading(prop: CataloguePropInfo, packNames: PackNames): PropHeading {
  const pack = packOf(prop.tileset) ?? "";
  const packName = pack === "" ? "" : (packNames.get(pack) ?? pack);
  const from = prop.path === undefined ? "sheet" : "path";
  const folders = prop.path === undefined ? [tilesetDisplayName(prop.tileset)] : foldersOf(prop.path);
  const parts = [packName, ...folders].filter((part) => part !== "");
  return {
    key: JSON.stringify([from, pack, ...folders.map((folder) => folder.toLowerCase())]),
    pack,
    packName,
    folders,
    from,
    label: parts.length === 0 ? tilesetDisplayName(prop.tileset) : parts.join(LABEL_SEPARATOR),
  };
}

/**
 * A prop's whole place: its heading's label and its name.
 *
 * @param prop The prop.
 * @param packNames Display names of packs by id.
 * @returns The place, `Forest Pack / Props / Static props / sheet1-sprites / barrel 1`.
 */
export function propLabel(prop: CataloguePropInfo, packNames: PackNames): string {
  return `${propHeading(prop, packNames).label}${LABEL_SEPARATOR}${prop.name}`;
}

/**
 * Orders two strings by code unit.
 *
 * @param a One string.
 * @param b The other.
 * @returns Negative, zero or positive.
 */
function byCodeUnit(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Orders two runs of digits by the number they write, of any length.
 *
 * @param a One run.
 * @param b The other.
 * @returns Negative, zero or positive; zero for `7` and `007`.
 */
function byNumber(a: string, b: string): number {
  const x = a.replace(LEADING_ZEROS, "");
  const y = b.replace(LEADING_ZEROS, "");
  return x.length !== y.length ? x.length - y.length : byCodeUnit(x, y);
}

/**
 * Natural order without regard to case or to leading zeros, read along both strings in lower case: where
 * both hold a run of digits the runs compare by their value, anywhere else the code units compare one by
 * one, so a space or a mark sorts before a digit (`waterwell - rope` before `waterwell 2 - rope`), and a
 * string that runs out first comes first.
 *
 * @param a One string.
 * @param b The other.
 * @returns Negative, zero or positive; zero for strings that differ only in case or leading zeros.
 */
function byFoldedNatural(a: string, b: string): number {
  const x = a.toLowerCase();
  const y = b.toLowerCase();
  let i = 0;
  let j = 0;
  while (i < x.length && j < y.length) {
    if (isDigit(x, i) && isDigit(y, j)) {
      const endX = digitsEnd(x, i);
      const endY = digitsEnd(y, j);
      const order = byNumber(x.slice(i, endX), y.slice(j, endY));
      if (order !== 0) {
        return order;
      }
      i = endX;
      j = endY;
    } else {
      const order = x.charCodeAt(i) - y.charCodeAt(j);
      if (order !== 0) {
        return order;
      }
      i += 1;
      j += 1;
    }
  }
  return x.length - i - (y.length - j);
}

/**
 * Whether a string holds a digit at an index.
 *
 * @param text The string.
 * @param index The index.
 * @returns True for `0` to `9`.
 */
function isDigit(text: string, index: number): boolean {
  const code = text.charCodeAt(index);
  return code >= 48 && code <= 57;
}

/**
 * Where the run of digits starting at an index ends.
 *
 * @param text The string.
 * @param from The index of the run's first digit.
 * @returns The index just past its last digit.
 */
function digitsEnd(text: string, from: number): number {
  let end = from;
  while (end < text.length && isDigit(text, end)) {
    end += 1;
  }
  return end;
}

/**
 * Natural order, total: digits compare by value and letters without regard to case (`barrel 2` before
 * `Barrel 10`), and strings that differ only in case or leading zeros are then ordered by code unit, so
 * only equal strings compare equal.
 *
 * @param a One string.
 * @param b The other.
 * @returns Negative, zero or positive.
 */
export function naturalCompare(a: string, b: string): number {
  return byFoldedNatural(a, b) || byCodeUnit(a, b);
}

/**
 * Orders two folder paths folder by folder in natural order without regard to case, a folder before its
 * subfolders.
 *
 * @param a One path's folders.
 * @param b The other's.
 * @returns Negative, zero or positive.
 */
function byFolders(a: readonly string[], b: readonly string[]): number {
  const count = Math.min(a.length, b.length);
  for (let i = 0; i < count; i += 1) {
    const order = byFoldedNatural(a[i] as string, b[i] as string);
    if (order !== 0) {
      return order;
    }
  }
  return a.length - b.length;
}

/**
 * Orders two headings: by pack display name, then pack id, headings by path before headings by sheet,
 * then folder by folder, and by key last, so two headings compare equal exactly when they are one.
 *
 * @param a One heading.
 * @param b The other.
 * @returns Negative, zero or positive.
 */
function byHeading(a: PropHeading, b: PropHeading): number {
  return byFoldedNatural(a.packName, b.packName) || byCodeUnit(a.pack, b.pack) || byCodeUnit(a.from, b.from) || byFolders(a.folders, b.folders) || byCodeUnit(a.key, b.key);
}

/**
 * Orders two props that share a place and a name by their tileset and tile, so no two props compare equal.
 *
 * @param a One prop.
 * @param b The other.
 * @returns Negative, zero or positive.
 */
function byFrame(a: CataloguePropInfo, b: CataloguePropInfo): number {
  return byCodeUnit(a.tileset, b.tileset) || a.tile - b.tile;
}

/**
 * Folder order: by heading, then by group, then by name, then by frame.
 *
 * @param a One prop with its heading.
 * @param b The other.
 * @returns Negative, zero or positive.
 */
function inFolderOrder(a: Headed, b: Headed): number {
  return byHeading(a.heading, b.heading) || naturalCompare(a.prop.group, b.prop.group) || naturalCompare(a.prop.name, b.prop.name) || byFrame(a.prop, b.prop);
}

/**
 * Name order: by group, then by heading, so a group of one name in two headings stays two runs side by side,
 * then by name, then by frame.
 *
 * @param a One prop with its heading.
 * @param b The other.
 * @returns Negative, zero or positive.
 */
function inNameOrder(a: Headed, b: Headed): number {
  return naturalCompare(a.prop.group, b.prop.group) || byHeading(a.heading, b.heading) || naturalCompare(a.prop.name, b.prop.name) || byFrame(a.prop, b.prop);
}

/**
 * The key a prop's group is known by in a sort: its heading and its group, since one group name can lie in
 * two headings.
 *
 * @param prop The prop.
 * @param heading Its heading.
 * @returns The key.
 */
function groupKey(prop: CataloguePropInfo, heading: PropHeading): string {
  return `${heading.key}\n${prop.group}`;
}

/**
 * The rank of each group in a playback order: the best rank of its props.
 *
 * @param headed The props with their headings.
 * @param ranks Rank by playback.
 * @returns Rank by group key.
 */
function groupRanks(headed: readonly Omit<Headed, "rank">[], ranks: ReadonlyMap<string, number>): Map<string, number> {
  const best = new Map<string, number>();
  for (const { prop, heading } of headed) {
    const key = groupKey(prop, heading);
    const rank = playbackRank(prop, ranks);
    best.set(key, Math.min(best.get(key) ?? rank, rank));
  }
  return best;
}

/**
 * The rank of a prop's playback in an order, props with no known playback last.
 *
 * @param prop The prop.
 * @param ranks Rank by playback.
 * @returns The rank.
 */
function playbackRank(prop: CataloguePropInfo, ranks: ReadonlyMap<string, number>): number {
  return (prop.plays === undefined ? undefined : ranks.get(prop.plays)) ?? ranks.size;
}

/**
 * How an order compares props, and the playback ranks its groups take, if it ranks them.
 */
interface OrderRule {
  readonly compare: (a: Headed, b: Headed) => number;
  readonly ranks: ReadonlyMap<string, number> | null;
}

const ORDER_RULES: ReadonlyMap<PropOrder, OrderRule> = new Map<PropOrder, OrderRule>([
  ["folder", { compare: inFolderOrder, ranks: null }],
  ["name", { compare: inNameOrder, ranks: null }],
  ["animated", { compare: (a, b) => a.rank - b.rank || inFolderOrder(a, b), ranks: ANIMATED_FIRST }],
  ["static", { compare: (a, b) => a.rank - b.rank || inFolderOrder(a, b), ranks: STATIC_FIRST }],
]);

/**
 * Sorts props in one of the palette's orders. The input is left as it was.
 *
 * @param props The props.
 * @param order The order.
 * @param packNames Display names of packs by id, which order the packs.
 * @returns The props in order.
 * @throws RangeError when the order is not one of `PROP_ORDERS`.
 */
export function sortProps(props: readonly CataloguePropInfo[], order: PropOrder, packNames: PackNames): CataloguePropInfo[] {
  const rule = ORDER_RULES.get(order);
  if (rule === undefined) {
    throw new RangeError(`prop order ${JSON.stringify(order)} is not one of ${PROP_ORDERS.map((o) => o.order).join(", ")}`);
  }
  const headed = props.map((prop) => ({ prop, heading: propHeading(prop, packNames) }));
  const ranks = rule.ranks === null ? null : groupRanks(headed, rule.ranks);
  return headed
    .map((entry): Headed => ({ ...entry, rank: ranks?.get(groupKey(entry.prop, entry.heading)) ?? 0 }))
    .sort(rule.compare)
    .map((entry) => entry.prop);
}

/**
 * The heading of each key as the first of some props spells it. Given a catalogue's props in its order, it
 * spells every heading as the catalogue lists it first (ADR-026 amendment, decision 1), which a palette
 * works out once per catalogue and hands to `groupByHeading`.
 *
 * @param props The props, in the order that decides the spelling.
 * @param packNames Display names of packs by id.
 * @returns The first heading of each key, by key, in the order the keys first appear.
 */
export function headingSpellings(props: readonly CataloguePropInfo[], packNames: PackNames): ReadonlyMap<string, PropHeading> {
  const spellings = new Map<string, PropHeading>();
  for (const prop of props) {
    const heading = propHeading(prop, packNames);
    if (!spellings.has(heading.key)) {
      spellings.set(heading.key, heading);
    }
  }
  return spellings;
}

/**
 * Groups props under their headings: headings in folder order, and each heading's props in the order given.
 * A heading is spelled as `spellings` holds it, so the catalogue's spellings (`headingSpellings` of its
 * props) keep a folder listed in two spellings the same whatever the sort or the filter; a heading they do
 * not hold is spelled as the first of its props given spells it.
 *
 * @param props The props, a catalogue's or some of them, in any order.
 * @param packNames Display names of packs by id.
 * @param spellings Headings by key, from `headingSpellings` of the catalogue's props.
 * @returns The headings with their props.
 */
export function groupByHeading(props: readonly CataloguePropInfo[], packNames: PackNames, spellings: ReadonlyMap<string, PropHeading> = new Map()): HeadedProps[] {
  const groups = new Map<string, { readonly heading: PropHeading; readonly props: CataloguePropInfo[] }>();
  for (const prop of props) {
    const heading = propHeading(prop, packNames);
    const group = groups.get(heading.key);
    if (group === undefined) {
      groups.set(heading.key, { heading: spellings.get(heading.key) ?? heading, props: [prop] });
    } else {
      group.props.push(prop);
    }
  }
  return [...groups.values()].sort((a, b) => byHeading(a.heading, b.heading));
}

/**
 * Whether a prop matches every word of a search: each word is found in its name, its group or, when the search
 * reads headings, the label of its heading. The heading is worked out only once a word misses the name and
 * the group, so a search most names answer costs little more than without headings.
 *
 * @param prop The prop.
 * @param words The words, in lower case and none empty; none matches everything.
 * @param headings Pack names to label headings with, or undefined when the search keeps to names.
 * @returns True when it matches.
 */
function matchesWords(prop: CataloguePropInfo, words: readonly string[], headings: PackNames | undefined): boolean {
  const name = prop.name.toLowerCase();
  const group = prop.group.toLowerCase();
  let label: string | null = null;
  for (const word of words) {
    if (name.includes(word) || group.includes(word)) {
      continue;
    }
    if (headings === undefined) {
      return false;
    }
    label ??= propHeading(prop, headings).label.toLowerCase();
    if (!label.includes(word)) {
      return false;
    }
  }
  return true;
}

/**
 * The words of a search: split by white space, in lower case. The prop palette and a state's frame picker
 * both read a search this way, so a frame is found by the same words in either.
 *
 * @param search The text.
 * @returns Its words; none for empty or blank text.
 */
export function searchWords(search: string): string[] {
  return search.toLowerCase().split(/\s+/).filter((word) => word !== "");
}

/**
 * The props a filter keeps, in the order given: the text, the tags and the packs must all match.
 *
 * @param props The props.
 * @param filter The filter.
 * @returns The props kept.
 */
export function filterProps(props: readonly CataloguePropInfo[], filter: PropFilter): CataloguePropInfo[] {
  const words = searchWords(filter.search ?? "");
  const tags = filter.tags ?? [];
  const packs = filter.packs ?? [];
  return props.filter(
    (prop) =>
      matchesWords(prop, words, filter.searchHeadings) &&
      (tags.length === 0 || propTags(prop).some((tag) => tags.includes(tag))) &&
      (packs.length === 0 || packs.includes(packOf(prop.tileset) ?? "")),
  );
}
